import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  hideSnackbar,
  showSnackbar,
  type SnackbarEntry,
} from '@/store/slices/snackbarSlice';
import { isNonProductionRuntime } from '@/lib/config/runtimeEnv';
import { useI18nText } from '@/lib/utils/i18nText';
import { cn } from '@/lib/utils/classNames';

type DebugWindow = Window & {
  __ggcDebug?: {
    triggerDummyApiErrorToast: () => void;
    triggerMultipleDummyApiErrorToasts: () => void;
  };
};

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

// Auto-dismiss timing per role
const TOAST_DURATION_NON_DEBUG_MS = 4000;
const TOAST_DURATION_DEBUG_MS = 6000;

function formatApiLabel(entry: SnackbarEntry): string {
  if (!entry.apiLabel) return '';
  return `[${entry.method ? `${entry.method} ` : ''}${entry.apiLabel}]`;
}

function toClipboardText(entry: SnackbarEntry): string {
  const meta = [
    entry.code != null ? `HTTP ${entry.code}` : null,
    entry.method,
    entry.apiLabel,
    timeFormatter.format(new Date(entry.timestamp)),
  ]
    .filter(Boolean)
    .join(' | ');
  return `${meta}\n${entry.text}`.trim();
}

export default function Snackbar() {
  const { t } = useI18nText();
  const dispatch = useAppDispatch();
  const isDebugUser = useAppSelector((s) =>
    Boolean(s.envs.currentUser?.permissions?.toBeOver9000),
  );
  const { open, current, duration } = useAppSelector((s) => s.snackbar);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !current) return;

    // Manual-only when duration explicitly 0 and not api-error
    if (current.kind === 'default' && duration === 0) return;

    const ms =
      duration > 0
        ? duration
        : current.kind === 'api-error'
          ? isDebugUser
            ? TOAST_DURATION_DEBUG_MS
            : TOAST_DURATION_NON_DEBUG_MS
          : 3000;

    const timer = window.setTimeout(() => dispatch(hideSnackbar()), ms);
    return () => window.clearTimeout(timer);
  }, [open, current, duration, isDebugUser, dispatch]);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(timer);
  }, [copied]);

  // Dev-mode debug hooks
  useEffect(() => {
    if (!isNonProductionRuntime()) return;

    const debugWindow = window as DebugWindow;
    debugWindow.__ggcDebug = {
      ...debugWindow.__ggcDebug,
      triggerDummyApiErrorToast: () => {
        dispatch(
          showSnackbar({
            text: 'Dummy API timeout for toast QA',
            kind: 'api-error',
            code: 504,
            method: 'POST',
            apiLabel: 'campaigns.getCampaigns',
            timestamp: Date.now(),
          }),
        );
      },
      triggerMultipleDummyApiErrorToasts: () => {
        const baseTimestamp = Date.now();
        const entries = [
          { code: 504, method: 'POST', apiLabel: 'campaigns.getCampaigns', text: 'Dummy timeout while loading campaigns' },
          { code: 502, method: 'GET', apiLabel: 'stats.loadOverview', text: 'Dummy upstream gateway failure' },
          { code: 429, method: 'POST', apiLabel: 'stats.analyzeComments', text: 'Dummy rate limit exceeded' },
        ];
        entries.forEach((entry, index) => {
          window.setTimeout(() => {
            dispatch(
              showSnackbar({
                ...entry,
                kind: 'api-error',
                timestamp: baseTimestamp + index,
              }),
            );
          }, index * 120);
        });
      },
    };

    return () => {
      delete debugWindow.__ggcDebug;
    };
  }, [dispatch]);

  if (!open || !current) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(toClipboardText(current));
      setCopied(true);
    } catch {
      // ignore
    }
  };

  // Success toast
  if (current.kind === 'success') {
    return (
      <div
        role="status"
        aria-live="polite"
        className="fixed bottom-6 left-1/2 z-[9998] max-w-[min(520px,92vw)] -translate-x-1/2 font-ggc"
      >
        <div className="flex items-start gap-3 rounded-ggc-md border border-ggc-green-light bg-ggc-green-light px-4 py-3 text-ggc-green shadow-ggc-md">
          <i aria-hidden className="fa fa-check-circle mt-[1px] text-[16px]" />
          <p className="min-w-0 whitespace-pre-line break-all text-[12px] font-medium">{current.text}</p>
          <button
            type="button"
            onClick={() => dispatch(hideSnackbar())}
            aria-label={t('Close', '닫기')}
            className="ml-2 text-current opacity-60 hover:opacity-100"
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  // Default (text-only) toast
  if (current.kind === 'default') {
    return (
      <div
        role="status"
        aria-live="polite"
        className="fixed bottom-6 left-1/2 z-[9998] -translate-x-1/2 font-ggc"
      >
        <div className="flex items-center gap-3 rounded-ggc-md border border-ggc-border-gray bg-ggc-main px-4 py-3 text-white shadow-ggc-md">
          <p className="text-[12px] font-medium">{current.text}</p>
          <button
            type="button"
            onClick={() => dispatch(hideSnackbar())}
            aria-label={t('Close', '닫기')}
            className="ml-2 text-current opacity-70 hover:opacity-100"
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  // API error toast — split by user role
  // Non-debug: generic concise error message, no details, auto-dismiss
  // Debug: full diagnostic with copy action; history accessible via ErrorBadge
  if (!isDebugUser) {
    return (
      <div
        role="alert"
        aria-live="assertive"
        className="fixed bottom-6 left-1/2 z-[9998] -translate-x-1/2 font-ggc"
      >
        <div className="flex items-center gap-3 rounded-ggc-md border border-ggc-red-light bg-ggc-red-light px-4 py-3 text-ggc-red shadow-ggc-md">
          <i aria-hidden className="fa fa-exclamation-circle text-[16px]" />
          <p className="text-[12px] font-medium">
            {t(
              'Something went wrong. Please try again.',
              '오류가 발생했습니다. 다시 시도해 주세요.',
            )}
          </p>
          <button
            type="button"
            onClick={() => dispatch(hideSnackbar())}
            aria-label={t('Close', '닫기')}
            className="ml-2 text-current opacity-60 hover:opacity-100"
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  // Debug user — detailed toast (top-center, dark)
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed left-1/2 top-6 z-[9998] w-[min(560px,90vw)] -translate-x-1/2 font-ggc"
    >
      <div className="overflow-hidden rounded-ggc-lg border border-ggc-main bg-ggc-main text-white shadow-ggc-lg">
        <div className="flex items-start gap-3 px-4 py-3">
          <i
            aria-hidden
            className="fa fa-exclamation-circle mt-[2px] text-[18px] text-ggc-red"
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 opacity-80 ggc-text-caption-s">
              {current.code != null && (
                <span className="inline-flex items-center rounded-ggc-sm bg-ggc-red px-[6px] py-[2px] font-semibold">
                  {current.code}
                </span>
              )}
              {current.apiLabel && (
                <span className="font-mono">{formatApiLabel(current)}</span>
              )}
              <span className="ml-auto">
                {timeFormatter.format(new Date(current.timestamp))}
              </span>
            </div>
            <p className="mt-1 break-words text-[13px] font-semibold leading-snug">
              {current.text}
            </p>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={() => void handleCopy()}
                className={cn(
                  'rounded-ggc-sm border border-white/20 bg-white/10 px-2 py-[3px] text-[10px] font-semibold transition-colors hover:bg-white/20',
                )}
              >
                {copied
                  ? t('Copied', '복사됨')
                  : t('Copy current error', '현재 에러 복사')}
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => dispatch(hideSnackbar())}
            aria-label={t('Close', '닫기')}
            className="text-white/60 transition-opacity hover:text-white"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
